import { formatCurrency, formatDate, formatNumber } from "../lib/format";
import type { EntityDossierSummary } from "../lib/entity-dossier";
import { Button } from "./ui/button";
import { StatusBadge } from "./ui/status-badge";

interface EntityDossierProps {
  dossier: EntityDossierSummary;
  onSelectTransaction: (transactionId: string) => void;
  onClose: () => void;
}

export function EntityDossier({ dossier, onSelectTransaction, onClose }: EntityDossierProps) {
  return (
    <section
      aria-label="Entity dossier"
      className="flex h-full min-h-0 flex-col border-l border-border bg-surface"
    >
      <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-3">
        <div className="min-w-0">
          <p className="text-[11px] uppercase tracking-wide text-muted-foreground">
            {dossier.ref.kind} dossier
          </p>
          <h2 className="truncate text-sm font-semibold text-foreground">{dossier.title}</h2>
          <p className="truncate text-xs text-muted-foreground">{dossier.subtitle}</p>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={onClose}>
          Close
        </Button>
      </header>

      <div className="grid grid-cols-2 gap-2 border-b border-border p-3 text-xs">
        <Metric label="Rows" value={formatNumber(dossier.rowCount)} />
        <Metric label="Latest filing" value={dossier.latestFilingDate ? formatDate(dossier.latestFilingDate) : "n/a"} />
        <Metric label="Informative buys" value={formatNumber(dossier.informativeBuyCount)} />
        <Metric label="Informative sells" value={formatNumber(dossier.informativeSellCount)} />
        <Metric label="High-score buys" value={formatNumber(dossier.highScoreBuyCount)} />
        <Metric label="Cluster rows" value={formatNumber(dossier.clusterRowCount)} />
        <Metric label="Planned" value={formatNumber(dossier.plannedCount)} />
        <Metric
          label="Total value"
          value={dossier.totalValue === null ? "n/a" : formatCurrency(dossier.totalValue)}
        />
        <Metric label="Insiders" value={formatNumber(dossier.distinctInsiders)} />
        <Metric label="Tickers" value={formatNumber(dossier.distinctTickers)} />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Recent transactions
        </p>
        <ul className="space-y-2">
          {dossier.rows.slice(0, 50).map((row) => (
            <li key={row.transactionId}>
              <button
                type="button"
                className="w-full rounded-md border border-border bg-surface-elevated px-3 py-2 text-left hover:border-accent"
                onClick={() => onSelectTransaction(row.transactionId)}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold">{row.ticker}</span>
                  <StatusBadge variant={row.signal.bucket.includes("buy") ? "buy" : row.signal.bucket.includes("sell") ? "sell" : "neutral"}>
                    {row.signal.bucket.split("_").join(" ")}
                  </StatusBadge>
                </div>
                <div className="mt-1 text-[11px] text-muted-foreground">
                  {row.insiderName} · {formatDate(row.filingDate)} ·{" "}
                  {row.value === null ? "n/a" : formatCurrency(row.value)}
                </div>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-surface-inset px-2 py-1.5">
      <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-0.5 font-medium tabular-nums text-foreground">{value}</div>
    </div>
  );
}
